import { CommandExecutionContext } from "types/commands";
import { StandardToolResult, createErrorResult } from "types/standardToolResults";
import { SimpleDocCommand } from "./types";
import { getDocContext as getDocCtx } from "../../services/doc/docApi";
import { DocumentUtils } from "../../services/doc/internal/yDocHighLevelAPI";
import * as Y from '../../../../shared/yjs';
import { nanoid } from 'nanoid';
import { API_BASE_URL } from '../../../../api/config';
import fetchService from '../../../../services/fetchService';

/**
 * Text Matching Helpers - Multi-stage fuzzy matching for robust text selection
 */

interface MatchResult {
  found: boolean;
  startPos: number;
  endPos: number;
  matchType: 'exact' | 'normalized' | 'unicode' | 'punctuation' | 'fuzzy';
  confidence: number;
}

/**
 * Position mapping - convert clean text positions to actual Y.js/ProseMirror positions
 */
interface PositionMapping {
  cleanStart: number;
  cleanEnd: number;
  actualStart: number;
  actualEnd: number;
}

/**
 * Normalize whitespace: collapse multiple spaces, convert newlines/tabs to spaces
 */
function normalizeWhitespace(text: string): string {
  return text
    .replace(/\s+/g, ' ')    // Multiple spaces → single space
    .replace(/\n/g, ' ')     // Newlines → spaces
    .replace(/\t/g, ' ')     // Tabs → spaces
    .trim();
}

/**
 * Normalize unicode characters: em dashes, smart quotes, ellipsis, etc.
 */
function normalizeUnicode(text: string): string {
  return text
    .replace(/['']/g, "'")        // Smart single quotes → regular apostrophe
    .replace(/[""]/g, '"')        // Smart double quotes → regular quotes
    .replace(/—/g, '-')           // Em dash → hyphen
    .replace(/–/g, '-')           // En dash → hyphen
    .replace(/…/g, '...')         // Ellipsis → three dots
    .normalize('NFKD')            // Unicode normalization
    .replace(/[\u0300-\u036f]/g, ''); // Remove diacritics
}

/**
 * Remove punctuation for flexible matching
 */
function removePunctuation(text: string): string {
  return text
    .replace(/[.,;:!?'"()[\]{}]/g, '')  // Remove punctuation
    .replace(/\s+/g, ' ')               // Normalize spaces
    .trim();
}

/**
 * Map position from normalized text back to original text
 * This handles cases where we found text in normalized version but need original position
 */
function mapNormalizedPositionToOriginal(
  originalText: string,
  normalizedPos: number,
  targetLength: number
): { start: number; end: number } | null {
  const originalPos = 0;
  let normalizedCount = 0;
  
  // Track whitespace to map positions
  const originalChars = originalText.split('');
  let foundStart = -1;
  
  for (let i = 0; i < originalChars.length; i++) {
    const char = originalChars[i];
    const isWhitespace = /\s/.test(char);
    
    // In normalized version, multiple whitespace becomes single space
    if (isWhitespace) {
      // Only count first whitespace in sequence
      if (i === 0 || !/\s/.test(originalChars[i - 1])) {
        if (normalizedCount === normalizedPos && foundStart === -1) {
          foundStart = i;
        }
        normalizedCount++;
      }
    } else {
      if (normalizedCount === normalizedPos && foundStart === -1) {
        foundStart = i;
      }
      normalizedCount++;
    }
    
    // Found the end position
    if (foundStart !== -1 && normalizedCount >= normalizedPos + targetLength) {
      return { start: foundStart, end: i + 1 };
    }
  }
  
  return null;
}

/**
 * Multi-stage text matching: tries multiple strategies to find target text in node
 */
function findTextInNode(nodeText: string, targetText: string): MatchResult {
  // STAGE 1: Exact match (fastest, preferred)
  const startPos = nodeText.indexOf(targetText);
  if (startPos !== -1) {
    return {
      found: true,
      startPos,
      endPos: startPos + targetText.length,
      matchType: 'exact',
      confidence: 1.0
    };
  }
  
  // STAGE 2: Normalized whitespace
  const normalizedNode = normalizeWhitespace(nodeText);
  const normalizedTarget = normalizeWhitespace(targetText);
  const normalizedPos = normalizedNode.indexOf(normalizedTarget);
  
  if (normalizedPos !== -1) {
    // Map back to original position
    const mapped = mapNormalizedPositionToOriginal(nodeText, normalizedPos, normalizedTarget.length);
    if (mapped) {
      return {
        found: true,
        startPos: mapped.start,
        endPos: mapped.end,
        matchType: 'normalized',
        confidence: 0.95
      };
    }
  }
  
  // STAGE 3: Unicode normalization (handles em dashes, smart quotes, etc.)
  const unicodeNode = normalizeUnicode(nodeText);
  const unicodeTarget = normalizeUnicode(targetText);
  const unicodePos = unicodeNode.indexOf(unicodeTarget);
  
  if (unicodePos !== -1) {
    return {
      found: true,
      startPos: unicodePos,
      endPos: unicodePos + unicodeTarget.length,
      matchType: 'unicode',
      confidence: 0.9
    };
  }
  
  // STAGE 4: Unicode + whitespace normalization (combined)
  const fullNormalizedNode = normalizeWhitespace(normalizeUnicode(nodeText));
  const fullNormalizedTarget = normalizeWhitespace(normalizeUnicode(targetText));
  const fullNormalizedPos = fullNormalizedNode.indexOf(fullNormalizedTarget);
  
  if (fullNormalizedPos !== -1) {
    const mapped = mapNormalizedPositionToOriginal(nodeText, fullNormalizedPos, fullNormalizedTarget.length);
    if (mapped) {
      return {
        found: true,
        startPos: mapped.start,
        endPos: mapped.end,
        matchType: 'fuzzy',
        confidence: 0.85
      };
    }
  }
  
  // STAGE 5: Punctuation-insensitive (last resort)
  const noPunctNode = removePunctuation(normalizeWhitespace(nodeText));
  const noPunctTarget = removePunctuation(normalizeWhitespace(targetText));
  const noPunctPos = noPunctNode.indexOf(noPunctTarget);
  
  if (noPunctPos !== -1) {
    // Approximate position (less accurate but better than nothing)
    const ratio = noPunctPos / noPunctNode.length;
    const approxStart = Math.floor(nodeText.length * ratio);
    return {
      found: true,
      startPos: approxStart,
      endPos: approxStart + targetText.length,
      matchType: 'punctuation',
      confidence: 0.75
    };
  }
  
  // All stages failed
  return {
    found: false,
    startPos: -1,
    endPos: -1,
    matchType: 'exact',
    confidence: 0
  };
}

/**
 * Map clean text positions to actual Y.js/ProseMirror positions
 * 
 * This walks the Y.js element structure and accounts for formatting nodes
 * like <strong>, <em>, etc. that take up positions in the actual document
 * but don't appear in the clean text.
 * 
 * @param element - The Y.js XmlElement to walk
 * @param cleanStart - Start position in clean text (no formatting)
 * @param cleanEnd - End position in clean text (no formatting)
 * @returns Mapped positions accounting for formatting nodes
 */
function mapCleanPositionsToActual(
  element: Y.XmlElement | Y.XmlFragment,
  cleanStart: number,
  cleanEnd: number
): PositionMapping | null {
  let cleanPos = 0;      // Position in clean text (no formatting)
  let actualPos = 0;     // Position in actual structure (with formatting)
  let mappedStart: number | null = null;
  let mappedEnd: number | null = null;

  /**
   * Recursively walk Y.js structure
   */
  const walk = (node: any): boolean => {
    if (!node) {
      return false; // Continue walking
    }

    // Check if this is a Y.XmlText node (has toDelta method)
    if (typeof (node as any).toDelta === 'function') {
      try {
        const delta = (node as any).toDelta();
        
        for (const op of delta) {
          const text = op.insert || '';
          const textLength = text.length;
          
          // Check if we're at the start position
          if (mappedStart === null && cleanPos + textLength > cleanStart) {
            const offset = cleanStart - cleanPos;
            mappedStart = actualPos + offset;
          }
          
          // Check if we're at the end position
          if (mappedEnd === null && cleanPos + textLength >= cleanEnd) {
            const offset = cleanEnd - cleanPos;
            mappedEnd = actualPos + offset;
          }
          
          // Advance both counters by the text length
          cleanPos += textLength;
          actualPos += textLength;
          
          // If we've found both positions, we're done
          if (mappedStart !== null && mappedEnd !== null) {
            return true; // Stop walking
          }
        }
      } catch (err) {
        // Fallback: treat as plain text
        const text = node.toString();
        const textLength = text.length;
        
        if (mappedStart === null && cleanPos + textLength > cleanStart) {
          mappedStart = actualPos + (cleanStart - cleanPos);
        }
        if (mappedEnd === null && cleanPos + textLength >= cleanEnd) {
          mappedEnd = actualPos + (cleanEnd - cleanPos);
        }
        
        cleanPos += textLength;
        actualPos += textLength;
      }
      
      return mappedStart !== null && mappedEnd !== null;
    }
    
    // Check if this is a Y.XmlElement (has length and get methods)
    if (typeof node.length === 'number' && typeof node.get === 'function') {
      // DON'T account for opening/closing tags - we want positions WITHIN the element
      // The renderer (commentPlugin.ts) will add elementPosition + 1 to account for the opening tag
      
      // Walk children
      for (let i = 0; i < node.length; i++) {
        const child = node.get(i);
        const shouldStop = walk(child);
        if (shouldStop) {
          return true;
        }
      }
      
      return mappedStart !== null && mappedEnd !== null;
    }
    
    return false;
  };

  // Start walking from the element
  walk(element);

  if (mappedStart !== null && mappedEnd !== null) {
    return {
      cleanStart,
      cleanEnd,
      actualStart: mappedStart,
      actualEnd: mappedEnd
    };
  }

  return null;
}

/**
 * Generate helpful suggestions when text not found
 */
function generateSuggestions(nodeText: string, targetText: string, maxSuggestions: number = 3): string[] {
  const targetWords = targetText.toLowerCase().split(/\s+/).filter(w => w.trim());
  const nodeWords = nodeText.toLowerCase().split(/\s+/);
  
  // Find substrings that contain some of the target words
  const suggestions: Array<{ text: string; score: number }> = [];
  const windowSize = Math.min(targetWords.length + 2, 15);
  
  for (let i = 0; i <= nodeWords.length - windowSize; i++) {
    const window = nodeWords.slice(i, i + windowSize);
    const windowText = window.join(' ');
    
    // Count how many target words appear in this window
    let score = 0;
    for (const targetWord of targetWords) {
      if (windowText.includes(targetWord)) {
        score++;
      }
    }
    
    if (score > 0) {
      // Get original text for this window
      const startIdx = nodeText.toLowerCase().indexOf(window[0]);
      const endIdx = nodeText.toLowerCase().indexOf(window[window.length - 1], startIdx) + window[window.length - 1].length;
      if (startIdx !== -1 && endIdx !== -1) {
        const originalText = nodeText.substring(startIdx, endIdx);
        suggestions.push({ text: originalText, score });
      }
    }
  }
  
  // Sort by score and return top suggestions
  suggestions.sort((a, b) => b.score - a.score);
  return suggestions.slice(0, maxSuggestions).map(s => s.text);
}

export const docsAddCommentCommand: SimpleDocCommand = {
  id: 'docs_add_comment',
  description: `Add inline comments to Docs.

**SUPPORTED ENTITIES:**
Docs (doc_*)

Use node IDs from doc_read_v2 to target specific text. Max 20 comments per call.

**EXAMPLE:**

{ "id": "doc_meadow_silver_compass", "comments": [{ "nodeId": "node_coral_quartz_fjord", "targetText": "This sentence needs work", "commentBody": "Consider revising for clarity" }] }`,
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'id',
        type: 'string',
        description: 'Document ID to comment on (e.g. "doc_meadow_silver_compass")',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Document ID cannot be empty';
          if (value.startsWith('note_') || value.startsWith('sheet_') || value.startsWith('canvas_') || value.startsWith('slide_') || value.startsWith('presentation_')) {
            return 'docs_add_comment is for Documents only. Comments not available for Notes/Sheets/Canvas/Slides.';
          }
          return null;
        }
      },
      {
        name: 'comments',
        type: 'array',
        description: `Array of comments (1-20 per call). Each comment: { nodeId, targetText, commentBody }

- nodeId: Node ID from doc_read_v2 (e.g. "node_coral_quartz_fjord")
- targetText: Exact text to highlight (copy verbatim, minimum 2 words)
- commentBody: Your feedback

Max 20 comments per call. For more, make multiple calls.`,
        required: true,
        items: {
          type: 'object',
          properties: {
            nodeId: {
              type: 'string',
              description: 'Node ID from doc_read_v2 (e.g. "node_coral_quartz_fjord")'
            },
            targetText: {
              type: 'string',
              description: 'Exact text to highlight from the node (copy verbatim from doc_read_v2)'
            },
            commentBody: {
              type: 'string',
              description: 'Your feedback/comment about the highlighted text'
            }
          },
          required: ['nodeId', 'targetText', 'commentBody']
        },
        validator: (value: any) => {
          if (!Array.isArray(value)) return 'Comments must be an array';
          if (value.length === 0) return 'Comments array cannot be empty';
          if (value.length > 20) return 'CRITICAL: Maximum 20 comments per call - tool will fail with more. Make multiple calls for additional comments.';
          
          // Validate each comment object structure
          for (let i = 0; i < value.length; i++) {
            const comment = value[i];
            if (typeof comment !== 'object' || comment === null) {
              return `Comment at index ${i} must be an object - see examples for correct format`;
            }
            if (!comment.nodeId) {
              return `Comment at index ${i} missing nodeId - see examples for correct format`;
            }
            if (!comment.targetText) {
              return `Comment at index ${i} missing targetText - must be exact quote from document`;
            }
            if (!comment.commentBody) {
              return `Comment at index ${i} missing commentBody - your feedback about the text`;
            }
          }
          return null;
        }
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: {
          id: 'doc-research-paper',
          comments: [
            {
              nodeId: 'node_methods_section',
              targetText: 'The study shows that people is affected',
              commentBody: 'Subject-verb agreement: "people are" not "people is"'
            },
            {
              nodeId: 'node_conclusion',
              targetText: 'This proves my point completely',
              commentBody: 'Too assertive - consider "suggests" or "indicates"'
            }
          ]
        },
        description: 'Multiple comments with exact text quotes from document'
      }
    ],
    
    bad: [
      {
        input: {
          id: 'doc-essay-draft',
          comments: [
            {
              nodeId: 'node_intro_paragraph',
              targetText: 'Grammar error',
              commentBody: 'Fix the grammar here'
            }
          ]
        },
        description: 'BAD: targetText must be exact quote from document, not a description'
      }
    ]
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { id, comments } = params;
    const authorName = 'River'; // Always River, not exposed to Claude

    try {
      // Validate comments array
      if (!Array.isArray(comments) || comments.length === 0) {
        return createErrorResult(
          'Invalid parameters',
          'Comments array is required and must not be empty',
          'validation'
        );
      }

      // Validate max 20 comments per call (STRICT LIMIT)
      if (comments.length > 20) {
        return createErrorResult(
          'Too many comments',
          `CRITICAL: Maximum 20 comments per call (received ${comments.length}). Tool will fail with more. Make multiple calls for additional comments.`,
          'validation'
        );
      }

      // Get document context using v5 pattern
      const { doc, error } = await getDocCtx(id, context);
      if (error || !doc) {
        return createErrorResult('Document not available', error || 'Could not access document', 'validation');
      }

      const results: any[] = [];

      // Process each comment
      for (const comment of comments) {
        // Validate required fields
        if (!comment.nodeId || !comment.targetText || !comment.commentBody) {
          results.push({
            success: false,
            error: 'Missing nodeId, targetText, or commentBody',
            nodeId: comment.nodeId || 'N/A',
            targetText: comment.targetText?.substring(0, 50) || 'N/A'
          });
          continue;
        }

        // Validate nodeId is a string
        if (typeof comment.nodeId !== 'string') {
          results.push({
            success: false,
            error: 'nodeId must be a string (e.g., "node_coral_quartz_fjord")',
            nodeId: comment.nodeId,
            targetText: comment.targetText.substring(0, 50)
          });
          continue;
        }

        // Validate target text length (minimum 2 words for reliable matching)
        const wordCount = comment.targetText.split(/\s+/).filter((w: string) => w.trim()).length;
        if (wordCount < 2) {
          results.push({
            success: false,
            error: `Target text must be at least 2 words for reliable matching (got ${wordCount} word${wordCount === 1 ? '' : 's'})`,
            nodeId: comment.nodeId,
            targetText: comment.targetText.substring(0, 50)
          });
          continue;
        }

        // Find the Y.js element directly by ID using DocumentUtils
        console.log(`[docs_add_comment] 🔍 Searching for node:`, comment.nodeId);
        const nodeLocation = DocumentUtils.findNodeById(doc.yxml, comment.nodeId);
        if (!nodeLocation) {
          console.error('[docs_add_comment] ❌ Node not found:', comment.nodeId);
          results.push({
            success: false,
            error: `Node "${comment.nodeId}" not found in document`,
            nodeId: comment.nodeId,
            targetText: comment.targetText.substring(0, 50)
          });
          continue;
        }

        console.log(`[docs_add_comment] ✅ Found node:`, {
          nodeId: comment.nodeId,
          elementType: nodeLocation.element.nodeName,
          elementId: nodeLocation.element.getAttribute?.('id') || 'no-id'
        });
        const element = nodeLocation.element;
        
        // Extract text using the SAME method as doc_read_v2
        // This ensures we get clean text without HTML tags, matching what AI sees
        let nodeText = '';
        try {
          nodeText = DocumentUtils.getTextContent(element);
          
          if (!nodeText || nodeText.trim().length === 0) {
            results.push({
              success: false,
              error: `Node ${comment.nodeId} appears to be empty`,
              nodeId: comment.nodeId,
              targetText: comment.targetText.substring(0, 50)
            });
            continue;
          }
        } catch (err) {
          console.error('[docs_add_comment] Error extracting text from element:', err);
          results.push({
            success: false,
            error: `Could not extract text from node ${comment.nodeId}: ${(err as Error).message}`,
            nodeId: comment.nodeId,
            targetText: comment.targetText.substring(0, 50)
          });
          continue;
        }
        
        // Use multi-stage fuzzy matching to find target text
        const matchResult = findTextInNode(nodeText, comment.targetText);
        
        if (!matchResult.found) {
          // Generate helpful suggestions
          const suggestions = generateSuggestions(nodeText, comment.targetText, 2);
          const suggestionText = suggestions.length > 0 
            ? `\nDid you mean: "${suggestions[0].substring(0, 60)}..."?`
            : '';
          
          results.push({
            success: false,
            error: `Target text not found in node ${comment.nodeId}${suggestionText}`,
            nodeId: comment.nodeId,
            targetText: comment.targetText.substring(0, 50),
            nodeText: nodeText.substring(0, 150),
            suggestions: suggestions.map(s => s.substring(0, 60))
          });
          continue;
        }
        
        const cleanStartPos = matchResult.startPos;
        const cleanEndPos = matchResult.endPos;

        // Map clean text positions to actual Y.js positions (accounting for formatting)
        const positionMapping = mapCleanPositionsToActual(element, cleanStartPos, cleanEndPos);
        
        if (!positionMapping) {
          results.push({
            success: false,
            error: `Failed to calculate accurate positions for comment in node ${comment.nodeId}`,
            nodeId: comment.nodeId,
            targetText: comment.targetText.substring(0, 50)
          });
          continue;
        }
        
        const actualStartPos = positionMapping.actualStart;
        const actualEndPos = positionMapping.actualEnd;

        // Add comment - FIRST persist to database, THEN add to Y.js
        try {
          const threadId = nanoid();
          const messageId = nanoid();
          const now = Date.now();

          // STEP 1: Persist to database FIRST (source of truth)
          let entityId: string | null = null;
          try {
            // fetchService already returns parsed JSON, not a Response object
            const entityData = await fetchService(`${API_BASE_URL}/comment-threads/docs/${doc.docId}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                docId: doc.docId,
                threadId: threadId,
                elementId: comment.nodeId,  // CRITICAL: Save the node ID!
                from: actualStartPos,
                to: actualEndPos,
                message: comment.commentBody,
                metadata: {
                  type: 'ai',
                  aiData: {
                    category: 'general',
                    reportId: null
                  }
                }
              })
            });
            
            console.log(`[docs_add_comment] 💾 Saved to backend:`, {
              threadId,
              elementId: comment.nodeId,
              from: actualStartPos,
              to: actualEndPos
            });

            entityId = entityData.thread._id || entityData.thread.id;
          } catch (persistError: any) {
            // If database fails, don't add to Y.js - fail the operation
            results.push({
              success: false,
              error: `Failed to persist comment to database: ${persistError.message}`,
              nodeId: comment.nodeId,
              targetText: comment.targetText.substring(0, 50)
            });
            continue; // Skip to next comment
          }

          // STEP 2: Only add to Y.js if database succeeded
          const commentThreads = doc.yDoc.getMap('commentThreads');
          const thread = new Y.Map();

          console.log(`[docs_add_comment] 📝 Creating comment thread:`, {
            threadId,
            nodeId: comment.nodeId,
            elementId: comment.nodeId,
            start: actualStartPos,
            end: actualEndPos,
            targetText: comment.targetText.substring(0, 40)
          });
          
          thread.set('id', threadId);
          thread.set('elementId', comment.nodeId); // Use nodeId directly
          thread.set('start', actualStartPos);  // Use mapped position!
          thread.set('end', actualEndPos);      // Use mapped position!
          thread.set('resolved', false);
          thread.set('createdAt', now);
          thread.set('updatedAt', now);
          thread.set('entityId', entityId); // Set entityId immediately

          // Add message
          const messages = new Y.Array();
          const message = new Y.Map();
          message.set('id', messageId);
          message.set('authorId', 'ai-assistant');
          message.set('authorName', authorName);
          message.set('body', comment.commentBody);
          message.set('createdAt', now);
          messages.push([message]);

          thread.set('messages', messages);
          commentThreads.set(threadId, thread);
          
          console.log(`[docs_add_comment] ✅ Added thread to Y.js commentThreads map:`, {
            threadId,
            nodeId: comment.nodeId,
            totalThreadsInMap: commentThreads.size
          });
          
          // CRITICAL: Emit event to trigger real-time UI update
          // The DocsEditor may be using a different Y.Doc instance
          window.dispatchEvent(new CustomEvent('doc:comment:added', {
            detail: {
              docId: doc.docId,
              threadId,
              nodeId: comment.nodeId
            }
          }));
          
          console.log(`[docs_add_comment] 📢 Emitted doc:comment:added event`);


          results.push({
            success: true,
            nodeId: comment.nodeId,
            targetText: comment.targetText.substring(0, 50),
            threadId,
            matchType: matchResult.matchType,
            confidence: matchResult.confidence
          });
        } catch (error: any) {
          console.error('[docs_add_comment] Failed to create comment:', error);
          results.push({
            success: false,
            error: `Failed to create comment: ${error.message}`,
            nodeId: comment.nodeId,
            targetText: comment.targetText.substring(0, 50)
          });
        }
      }

      // Summarize results
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;
      const executionTime = Date.now() - startTime;

      let message = `Added ${successCount} comment(s)`;
      if (failCount > 0) {
        message += `, ${failCount} failed`;
        const firstError = results.find(r => !r.success);
        if (firstError) {
          message += `\nFirst error: ${firstError.error}`;
        }
      }

      return {
        status: failCount === 0 ? 'success' : (successCount > 0 ? 'partial' : 'error'),
        message,
        format: 'plaintext',
        data: {
          successCount,
          failCount,
          executionTime,
          results
        }
      };

    } catch (error: any) {
      console.error('[docs_add_comment] ERROR:', error);
      return createErrorResult('Failed to add comment', error.message, 'execution');
    }
  }
};

